/**
 * Customer API Service
 *
 * Handles API calls for customer lookups.
 * Used in the booking pre-step to find existing customers and their vehicles.
 *
 * Lookup is an OR over indexed columns (phone, email) — at least one of
 * phone or email MUST be supplied. An optional `name` param narrows the
 * match further. Name on its own is rejected by the backend with 400 to
 * protect the DB from un-indexed full-table LIKE scans.
 */

import axiosInstance from './axiosInstance';


/**
 * Look up a customer by phone number within the current organisation.
 *
 * Returns the customer and all their owned vehicles on success.
 * Throws with response.status === 404 when no customer is found.
 *
 * @param {string} phone - Raw phone number (spaces/dashes are OK — backend normalises)
 * @param {string} [name] - Optional name to narrow the match (case-insensitive contains)
 * @returns {Promise<CustomerLookupResponse>}
 */
export const lookupCustomerByPhoneApi = async (phone, name) => {
    const params = { phone };
    if (name) params.name = name;
    const response = await axiosInstance.get('/customers/lookup', { params });
    return response.data;
};

/**
 * Look up a customer by email address within the current organisation.
 *
 * @param {string} email
 * @param {string} [name] - Optional name to narrow the match (case-insensitive contains)
 * @returns {Promise<CustomerLookupResponse>}
 */
export const lookupCustomerByEmailApi = async (email, name) => {
    const params = { email };
    if (name) params.name = name;
    const response = await axiosInstance.get('/customers/lookup', { params });
    return response.data;
};

/**
 * Generic lookup — supply phone and/or email, optionally narrowed by name.
 *
 * Backend rules:
 *   - At least one of phone or email is required (name alone → 400)
 *   - When both phone and email are supplied, phone is tried first
 *   - When name is supplied, the matched customer's name must also contain it
 *
 * @param {{ phone?: string, email?: string, name?: string }} params
 * @returns {Promise<CustomerLookupResponse>}
 */
export const lookupCustomerApi = async ({ phone, email, name }) => {
    const params = {};
    if (phone) params.phone = phone;
    if (email) params.email = email;
    if (name)  params.name  = name;
    const response = await axiosInstance.get('/customers/lookup', { params });
    return response.data;
};

/**
 * Search customers by partial name, phone, and/or vehicle rego.
 *
 * @param {object} params
 * @param {string} [params.name]   - Partial name (matches first OR last name, case-insensitive)
 * @param {string} [params.phone]  - Partial phone match
 * @param {string} [params.rego]   - Partial rego match against any of the customer's vehicles
 * @returns {Promise} resolves to an array of customer objects, each with their vehicles
 */
export const searchCustomers = ({ name = '', phone = '', rego = '' } = {}) => {
    const params = {};
    if (name  && name.trim())  params.name  = name.trim();
    if (phone && phone.trim()) params.phone = phone.trim();
    if (rego  && rego.trim())  params.rego  = rego.trim().toUpperCase();
    return axiosInstance.get('/customers/search', { params });
};


const customerApi = {
    lookupCustomerByPhoneApi,
    lookupCustomerByEmailApi,
    lookupCustomerApi,
    searchCustomers
};

export default customerApi;
/**
 * Browse customers for the Customers screen — one tab per `fleet` value.
 *
 * Unlike searchCustomers, this does not require a query term: it lists. `search` filters
 * on name or phone, the two things staff have to hand when a customer rings.
 *
 * @param {{fleet?: boolean, search?: string, page?: number, size?: number}} [opts]
 * @returns {Promise<{page:number,size:number,totalItems:number,totalPages:number,
 *   customers:{uuid,customerType,businessName,firstName,lastName,fullName,phone,email,
 *              address,suburb,state,postcode,createdAt,vehicles:object[]}[]}>}
 */
export const listCustomersApi = async ({ fleet, search, createdFrom, createdTo, page, size } = {}) => {
    const response = await axiosInstance.get('/customers', {
        params: { fleet, search, createdFrom, createdTo, page, size },
    });
    return response.data;
};

/**
 * Correct a customer's details — the repair path for bad data.
 *
 * PARTIAL update: omit a field to leave it alone. Sending null/'' for email clears it;
 * omitting it entirely does not.
 *
 * A Customer is SHARED across bookings and invoices, and invoices render the name at read
 * time — so a rename changes what past invoices display. That is the point when fixing a
 * mis-heard name, but it is not a local edit.
 */
export const updateCustomerApi = async (uuid, data) => {
    const response = await axiosInstance.put(`/customers/${uuid}`, data);
    return response.data;
};

/**
 * Delete a customer, their vehicles and their service history.
 *
 * Requires the `customer:delete` permission — the server rejects with 403 otherwise,
 * so hiding the button is presentation only, never the control.
 *
 * ⚠️ This also deletes every VEHICLE the customer owns, via a cascade on the customer
 * record. The confirmation copy must say so.
 *
 * Rejects with 400 when the customer still has invoices, jobs or bookings — the message
 * names how many of each — or when they still have a fleet portal login. Surface
 * `error.response.data.message` verbatim; it is what tells staff which screen to clear.
 *
 * @returns {Promise<{ uuid: string, serviceRecordsDeleted: number }>}
 */
export const deleteCustomerApi = async (uuid) => {
    const response = await axiosInstance.delete(`/customers/${uuid}`);
    return response.data;
};
